import React from "react";
import { Loader2, FileText } from "lucide-react";
import { useAppAuth } from "@/components/AuthProvider";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

interface Props {
  children: React.ReactNode;
}

/**
 * Wrap any mentorship page with this component to enforce Clerk-based access control.
 * While Clerk is loading it shows a spinner. If the viewer is **not** a mentorship
 * member it displays the polished "Premium Content" marketing panel.  Otherwise
 * it simply renders its children.
 */
export default function MentorshipAccessGate({ children }: Props) {
  const { isMentorshipMember, isLoading } = useAppAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!isMentorshipMember) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="container mx-auto px-4 py-10 flex-grow flex items-center justify-center">
          <div className="bg-white border rounded-lg shadow-lg p-8 max-w-2xl w-full text-center">
            <div className="mx-auto w-16 h-16 bg-amber-100 flex items-center justify-center rounded-full mb-6">
              <FileText className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="text-3xl font-serif font-bold text-[#2C3E50] mb-4">Premium Content Access</h1>
            <p className="text-lg text-[#7F8C8D] mb-6">
              This lecture is available exclusively to Next Generation Medicine mentorship members.
              Join our mentorship program to access this and other expert-led educational content.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/login">
                <Button variant="outline" className="w-full sm:w-auto">
                  Sign In
                </Button>
              </Link>
              <Link href="/membership">
                <Button className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600">
                  View Membership Options
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return <>{children}</>;
}
